import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

interface Props {
  city: string;
  state: string;
  value: string;
  onChange: (neighborhood: string, lat?: number, lng?: number) => void;
}

interface Suggestion {
  display_name: string;
  neighborhood: string;
  lat: number;
  lon: number;
}

export default function NeighborhoodAutocomplete({ city, state, value, onChange }: Props) {
  const [input, setInput] = useState(value || "");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setInput(value || ""); }, [value]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  async function search(q: string) {
    if (!q || q.length < 3 || !city) return;
    setLoading(true);
    try {
      const query = `${q}, ${city}, ${state}, Brasil`;
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&countrycodes=br`,
        { headers: { "Accept-Language": "pt-BR" } }
      );
      const data = await res.json();
      const results: Suggestion[] = data
        .filter((item: any) => item.address)
        .map((item: any) => ({
          display_name: item.display_name,
          neighborhood: item.address.suburb || item.address.neighbourhood || item.address.quarter || item.address.village || q,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
        }));
      setSuggestions(results);
      setOpen(results.length > 0);
    } catch { setSuggestions([]); }
    setLoading(false);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setInput(val);
    onChange(val);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => search(val), 500);
  }

  function handleSelect(s: Suggestion) {
    setInput(s.neighborhood);
    onChange(s.neighborhood, s.lat, s.lon);
    setOpen(false);
    setSuggestions([]);
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Bairro (opcional)"
          value={input}
          onChange={handleChange}
          className="pl-9"
          disabled={!city}
        />
        {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">...</span>}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full bg-card border border-border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button key={i} type="button" onClick={() => handleSelect(s)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary transition-colors border-b border-border/50 last:border-0">
              <span className="font-medium">{s.neighborhood}</span>
              <span className="text-xs text-muted-foreground block truncate">{s.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
